/**
 * Couverture fixe (fibre / THD / HD) — ARCEP « Ma connexion Internet ».
 *
 * Source : ARCEP publication mensuelle du fichier
 * `commune_debit_filaire.csv`, exposé en téléchargement direct sur
 * `data.arcep.fr/fixe/maconnexioninternet/statistiques/last/commune/…`.
 * CORS ✅, anonyme, ~2,4 MB décompressé, ~35 000 lignes (1 par commune).
 *
 * Schéma du CSV (séparateur `;`) — le fichier livré par l'ARCEP contient :
 *   code_insee ; nom_com ; code_dep ; code_reg ; nbr ; type ;
 *   inel_hd ; elig_hd05 ; elig_hd3 ; elig_bhd8 ;
 *   elig_thd30 ; elig_thd100 ; elig_thd1g ; date
 *
 * `nbr` est le nombre de locaux de la commune ; chaque colonne `elig_*` est
 * un nombre de locaux éligibles à la borne concernée. Les bornes sont
 * cumulatives sur les débits descendants : un local fibre éligible à 1 Gbps
 * est aussi compté dans `elig_thd100`, `elig_thd30`, `elig_bhd8`, etc.
 * On dérive donc les % fibre / THD / HD / rien de manière non cumulative :
 *
 *   fiber_pct    = elig_thd1g / nbr               — > 1 Gbps (pratiquement FTTH)
 *   thd_pct      = elig_thd30 - elig_thd1g        — THD hors fibre (câble / 4G+)
 *   hd_pct       = elig_hd3 - elig_thd30          — HD simple 3–30 Mbps
 *   nothing_pct  = inel_hd / nbr                  — inéligibles au haut débit
 *
 * Comme le CSV couvre toute la France en une requête, on télécharge une
 * seule fois par session (mémoire) et on indexe par INSEE. Le hook utilise
 * `useEnvBlock` pour persister le profil commune-par-commune dans Firestore
 * aux sessions suivantes — le CSV n'est alors jamais re-téléchargé pour une
 * fiche déjà ouverte.
 *
 * TTL : 30 jours — ARCEP publie mensuellement, on synchronise la même
 * cadence.
 */

import { useMemo } from "react";
import { useEnvBlock } from "./shared";
import type { GeocodeResult } from "../types";

// We must bypass `data.arcep.fr/.../statistiques/last/...` — it returns a
// 302 redirect to `object.files.data.gouv.fr` but the 302 itself carries NO
// `Access-Control-Allow-Origin` header, so the browser refuses to follow it.
// The final bucket domain does serve CORS correctly (even on 404s), so we
// hit it directly. The catch : the URL encodes the publication quarter, so
// we have to resolve "what's the latest quarter" client-side. We probe a
// short list of candidates newest-first and memoize the winning quarter in
// localStorage for later sessions.
const ARCEP_BUCKET_BASE =
  "https://object.files.data.gouv.fr/arcep/fixe/maconnexioninternet/statistiques";
const ARCEP_FILENAME = "commune/commune_debit_filaire.csv";
const ARCEP_QUARTER_LS_KEY = "pluview:arcep:quarter";

const ARCEP_TTL_DAYS = 30;

export type ArcepData = {
  insee: string;
  commune: string;
  // Total number of premises in the commune (ARCEP "locaux").
  total_locaux: number;
  // Percentages, 0–100. Sum ≈ 100 within rounding error (ARCEP itself
  // doesn't always balance to exactly 100 % because of reclassifications
  // mid-month — we trust the source).
  fiber_pct: number;
  thd_pct: number;
  hd_pct: number;
  nothing_pct: number;
  // Observation date from the CSV's `date` column — YYYY-MM-DD or similar,
  // ARCEP formatting varies. Shown raw as a disclosure in the card footer.
  observationDate: string;
};

// ─── Session cache ──────────────────────────────────────────────────────

type IndexedRow = {
  insee: string;
  commune: string;
  total_locaux: number;
  elig_thd1g: number;
  elig_thd30: number;
  elig_hd3: number;
  inel_hd: number;
  date: string;
};

// Module-level promise so concurrent hook calls within the same session
// share the download and parse — no race, no duplicate 2,4 MB transfers.
let indexPromise: Promise<Map<string, IndexedRow>> | null = null;

function loadIndex(): Promise<Map<string, IndexedRow>> {
  if (indexPromise) return indexPromise;
  indexPromise = (async () => {
    const { text } = await downloadLatestCsv();
    return parseCsv(text);
  })().catch((e) => {
    // Reset the shared promise on failure so the next hook call can retry —
    // otherwise a single 429 on app open would poison the session forever.
    indexPromise = null;
    throw e;
  });
  return indexPromise;
}

/**
 * Try a sequence of quarter-encoded URLs on the bucket and return the first
 * 200. We start with the quarter we succeeded on last time (if any) then
 * walk backwards from "current quarter" — at most 5 candidates so the
 * worst-case first-load latency stays bounded (~5 × 400 ms RTT).
 *
 * ARCEP publishes each quarter's CSV roughly 2–3 months after the quarter
 * ends, so the current quarter is usually NOT yet available — we still
 * include it in the candidates because a lucky visit right after publication
 * would pay for the robustness of the scheme.
 */
async function downloadLatestCsv(): Promise<{ text: string; quarter: string }> {
  const candidates = candidateQuarters(new Date());

  // Prepend the last-known-good quarter so returning users hit their cached
  // URL on the first try — 0 extra round-trip in the steady state.
  const remembered = safeReadLocalStorage(ARCEP_QUARTER_LS_KEY);
  if (remembered && !candidates.includes(remembered)) {
    candidates.unshift(remembered);
  } else if (remembered) {
    // Bump it to the front without duplicating.
    const idx = candidates.indexOf(remembered);
    if (idx > 0) {
      candidates.splice(idx, 1);
      candidates.unshift(remembered);
    }
  }

  let lastErr: unknown = null;
  for (const q of candidates) {
    const url = `${ARCEP_BUCKET_BASE}/${q}/${ARCEP_FILENAME}`;
    try {
      const res = await fetch(url, { signal: AbortSignal.timeout(30_000) });
      if (res.status === 404) {
        // Expected on future / not-yet-published quarters — keep probing.
        lastErr = new Error(`arcep ${q} HTTP 404`);
        continue;
      }
      if (!res.ok) {
        lastErr = new Error(`arcep ${q} HTTP ${res.status}`);
        continue;
      }
      const text = await res.text();
      safeWriteLocalStorage(ARCEP_QUARTER_LS_KEY, q);
      return { text, quarter: q };
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr instanceof Error
    ? lastErr
    : new Error("arcep : aucun trimestre accessible");
}

/**
 * Candidate publication quarters, newest-first, 5 deep — covers a full year
 * of backlog which is much more than ARCEP has ever slipped.
 */
function candidateQuarters(now: Date): string[] {
  const y = now.getUTCFullYear();
  const m = now.getUTCMonth(); // 0-based
  const q = Math.floor(m / 3) + 1; // 1-based
  const out: string[] = [];
  let py = y;
  let pq = q;
  for (let i = 0; i < 5; i++) {
    out.push(`${py}_T${pq}`);
    pq -= 1;
    if (pq === 0) {
      pq = 4;
      py -= 1;
    }
  }
  return out;
}

function safeReadLocalStorage(key: string): string | null {
  try {
    return typeof window !== "undefined" ? window.localStorage.getItem(key) : null;
  } catch {
    return null;
  }
}

function safeWriteLocalStorage(key: string, value: string): void {
  try {
    if (typeof window !== "undefined") window.localStorage.setItem(key, value);
  } catch {
    // ignored — Safari private mode + storage-full both throw here.
  }
}

/**
 * Minimal CSV parser tailored to the ARCEP file.
 *
 * The ARCEP CSV never quotes fields (only `nom_com` could need it — town
 * names with commas are rare, and the ARCEP export replaces them with `-`
 * anyway). So the naive `split(';')` is safe and much faster than pulling
 * in a CSV library. If future ARCEP exports introduce quoting this helper
 * needs upgrading.
 */
function parseCsv(text: string): Map<string, IndexedRow> {
  const lines = text.split(/\r?\n/);
  if (lines.length < 2) return new Map();

  const header = lines[0].split(";").map((c) => c.trim().toLowerCase());
  const idx = (name: string) => header.indexOf(name);

  const iInsee = idx("code_insee");
  const iNom = idx("nom_com");
  const iNbr = idx("nbr");
  const iInel = idx("inel_hd");
  const iHd3 = idx("elig_hd3");
  const iThd30 = idx("elig_thd30");
  const iThd1g = idx("elig_thd1g");
  const iDate = idx("date");

  if (iInsee < 0 || iNbr < 0 || iThd1g < 0) {
    // Columns we strictly depend on are missing — bail early so the caller
    // gets a clean error instead of a map full of NaN.
    throw new Error("arcep CSV: unexpected header");
  }

  const map = new Map<string, IndexedRow>();
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i];
    if (!line) continue;
    const cols = line.split(";");
    const insee = cols[iInsee]?.trim();
    if (!insee) continue;
    const total = toNumber(cols[iNbr]);
    if (total <= 0) continue; // empty or malformed row
    map.set(insee, {
      insee,
      commune: (cols[iNom] ?? "").trim(),
      total_locaux: total,
      elig_thd1g: toNumber(cols[iThd1g]),
      elig_thd30: toNumber(cols[iThd30]),
      elig_hd3: toNumber(cols[iHd3]),
      inel_hd: toNumber(cols[iInel]),
      date: (cols[iDate] ?? "").trim(),
    });
  }
  return map;
}

function toNumber(raw: string | undefined): number {
  if (!raw) return 0;
  // ARCEP uses `,` as decimal separator — normalize before Number().
  const n = Number(raw.replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

// ─── Fetch ──────────────────────────────────────────────────────────────

export async function fetchArcep(insee: string): Promise<ArcepData> {
  const index = await loadIndex();
  const row = index.get(insee);
  if (!row) {
    throw new Error(`arcep: INSEE ${insee} absent du jeu de données`);
  }
  const n = row.total_locaux || 1; // guard against divide-by-zero
  // Derive non-cumulative bands from the ARCEP cumulative eligibility
  // counts. Clamp to [0, 100] because ARCEP data occasionally reports a
  // `thd1g` slightly above `thd30` (month-to-month re-enumeration of the
  // local pool) — we prefer a visibly off-by-rounding card to a crash.
  const fiber_pct = pct(row.elig_thd1g, n);
  const thd_pct = pct(Math.max(0, row.elig_thd30 - row.elig_thd1g), n);
  const hd_pct = pct(Math.max(0, row.elig_hd3 - row.elig_thd30), n);
  const nothing_pct = pct(row.inel_hd, n);
  return {
    insee,
    commune: row.commune,
    total_locaux: row.total_locaux,
    fiber_pct,
    thd_pct,
    hd_pct,
    nothing_pct,
    observationDate: row.date,
  };
}

function pct(numerator: number, denominator: number): number {
  return Math.round((numerator / denominator) * 1000) / 10;
}

// ─── Hook ───────────────────────────────────────────────────────────────

/**
 * `useArcep` — cache-first loader keyed on INSEE. The whole CSV is
 * downloaded once per session into an in-memory Map, but the per-commune
 * profile is also persisted to Firestore (`env/arcep`) so a returning
 * visitor sees the card fill instantly without any network round-trip.
 */
export function useArcep(
  ficheId: string | null,
  geocode: GeocodeResult | null,
) {
  const opts = useMemo(() => {
    if (!ficheId || !geocode) return null;
    const insee = geocode.insee;
    return {
      ficheId,
      blockId: "arcep" as const,
      // Bumped v1→v2 when we switched from the (CORS-broken) `data.arcep.fr`
      // URL to the direct bucket URL — any error docs cached from the old
      // code path are treated as key-mismatch and refetched on next access.
      inputsKey: `v2|${insee}`,
      ttlDays: ARCEP_TTL_DAYS,
      fetcher: () => fetchArcep(insee),
    };
  }, [ficheId, geocode]);

  return useEnvBlock<ArcepData>(opts);
}
